import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Repeat2,
  Pause,
  Play,
  X,
  CheckCircle2,
  Calendar,
  Home,
  MapPin,
  Loader2,
  RotateCcw,
} from "lucide-react";
import { logError } from "@/lib/log";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLE = {
  active: "bg-green-100 text-green-700",
  paused: "bg-amber-100 text-amber-700",
  cancellation_requested: "bg-red-100 text-red-700",
};
const STATUS_LABEL = {
  active: "Active",
  paused: "Paused",
  cancellation_requested: "Cancelling",
};

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch (_e) {
    return iso;
  }
};

export const SubscriptionsTab = ({ subscriptions, onChanged }) => {
  const [cancelTarget, setCancelTarget] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const callAction = async (sub, action, successMsg) => {
    setBusyId(sub.id);
    try {
      await axios.post(`${API}/customer/subscriptions/${sub.id}/${action}`);
      toast.success(successMsg);
      onChanged();
    } catch (err) {
      logError(`subscription ${action} failed`, err);
      toast.error(err?.response?.data?.detail || "Action failed");
    } finally {
      setBusyId(null);
      setCancelTarget(null);
    }
  };

  if (!subscriptions || subscriptions.length === 0) {
    return (
      <Card className="bg-white border-[#E2E8E0]" data-testid="subscriptions-empty">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A2E12]">
            <Repeat2 className="w-5 h-5 text-[#2D5016]" />
            Manage Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-12">
            <Repeat2 className="w-12 h-12 text-[#E2E8E0] mx-auto mb-4" />
            <p className="text-[#5C6B57] mb-1">No active subscriptions yet.</p>
            <p className="text-sm text-[#5C6B57]">
              Book a recurring Standard or Deep Clean from the Offers page to start a subscription.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card className="bg-white border-[#E2E8E0]" data-testid="subscriptions-panel">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-[#1A2E12]">
            <Repeat2 className="w-5 h-5 text-[#2D5016]" />
            Manage Subscriptions
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {subscriptions.map((sub) => {
            const status = sub.subscription_status || "active";
            const isCancelling = status === "cancellation_requested";
            const isPaused = status === "paused";
            const busy = busyId === sub.id;

            return (
              <div
                key={sub.id}
                className="border border-[#E2E8E0] rounded-xl p-5"
                data-testid={`subscription-row-${sub.id}`}
              >
                <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-semibold text-[#1A2E12]">{sub.service_name}</h3>
                      <Badge
                        className={`${STATUS_STYLE[status] || "bg-gray-100 text-gray-700"} font-medium`}
                        data-testid={`subscription-status-${sub.id}`}
                      >
                        {STATUS_LABEL[status] || status}
                      </Badge>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-sm text-[#5C6B57]">
                      <span className="flex items-center gap-1">
                        <Home className="w-3.5 h-3.5" />
                        {sub.bedrooms} bed
                      </span>
                      {sub.postcode && (
                        <span className="flex items-center gap-1">
                          <MapPin className="w-3.5 h-3.5" />
                          {sub.postcode}
                        </span>
                      )}
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        Next visit: {formatDate(sub.next_visit)}
                      </span>
                    </div>
                    <p className="mt-2 text-sm font-medium text-[#2D5016]">
                      £{Number(sub.total_per_visit || 0).toFixed(2)} per visit
                    </p>

                    {isPaused && (
                      <p className="mt-2 text-xs text-amber-700">
                        Paused until {formatDate(sub.paused_until)} — visits will not be scheduled in this window.
                      </p>
                    )}
                    {isCancelling && (
                      <p className="mt-2 text-xs text-red-700">
                        Cancellation scheduled. Final billable cycle ends on <strong>{formatDate(sub.cancellation_effective_date)}</strong> (30-day notice per Subscription Policy). You can undo any time before this date.
                      </p>
                    )}
                  </div>

                  <div className="flex flex-wrap gap-2 md:justify-end">
                    {isCancelling ? (
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          callAction(sub, "undo-cancel", "Cancellation reversed")
                        }
                        className="border-[#2D5016] text-[#2D5016] rounded-full"
                        data-testid={`subscription-undo-${sub.id}`}
                      >
                        {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <RotateCcw className="w-4 h-4 mr-1" />}
                        Undo cancellation
                      </Button>
                    ) : (
                      <>
                        {isPaused ? (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => callAction(sub, "resume", "Subscription resumed")}
                            className="border-[#2D5016] text-[#2D5016] rounded-full"
                            data-testid={`subscription-resume-${sub.id}`}
                          >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4 mr-1" />}
                            Resume
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            disabled={busy}
                            onClick={() => callAction(sub, "pause", "Subscription paused")}
                            className="border-[#E2E8E0] text-[#5C6B57] rounded-full"
                            data-testid={`subscription-pause-${sub.id}`}
                          >
                            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Pause className="w-4 h-4 mr-1" />}
                            Pause
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          disabled={busy}
                          onClick={() => setCancelTarget(sub)}
                          className="border-red-300 text-red-700 hover:bg-red-50 rounded-full"
                          data-testid={`subscription-cancel-${sub.id}`}
                        >
                          <X className="w-4 h-4 mr-1" />
                          Cancel
                        </Button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}

          <p className="text-xs text-[#5C6B57] pt-2 border-t border-[#E2E8E0]">
            Subscription Policy: cancel any time with 30 calendar days&apos; notice; pauses are valid for up to 90 days. See full terms at <a className="underline" href="/legal#subscription">/legal#subscription</a>.
          </p>
        </CardContent>
      </Card>

      <AlertDialog open={!!cancelTarget} onOpenChange={() => setCancelTarget(null)}>
        <AlertDialogContent data-testid="cancel-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-[#1A2E12] flex items-center gap-2">
              <CheckCircle2 className="w-5 h-5 text-[#2D5016]" />
              Cancel subscription with 30 days&apos; notice?
            </AlertDialogTitle>
            <AlertDialogDescription className="text-[#5C6B57]">
              We&apos;ll continue scheduled visits for the next 30 days, then stop. You can undo this any time before the effective date. No further charges after that.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-confirm-no">Keep subscription</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => callAction(cancelTarget, "request-cancel", "Cancellation scheduled — see your dashboard for the effective date")}
              className="bg-red-600 hover:bg-red-700 text-white"
              data-testid="cancel-confirm-yes"
            >
              Confirm cancellation
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};

export default SubscriptionsTab;
